import React, {
    useEffect,
    useRef,
    useState,
    forwardRef,
    useReducer,
    useImperativeHandle
} from "react";
import "@arcgis/core/assets/esri/themes/light/main.css";
import ArcGISMap from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
// Web components replace all deprecated @arcgis/core/widgets/* classes
import { defineCustomElements } from "@arcgis/map-components/dist/loader";
import { defineCustomElements as defineCalciteElements } from "@esri/calcite-components/dist/loader";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Extent from "@arcgis/core/geometry/Extent";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import Point from "@arcgis/core/geometry/Point";
import * as webMercatorUtils from "@arcgis/core/geometry/support/webMercatorUtils";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import PopupTemplate from "@arcgis/core/PopupTemplate";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import Polygon from "@arcgis/core/geometry/Polygon";
import WebMap from "@arcgis/core/WebMap";
import Basemap from "@arcgis/core/Basemap";
import VectorTileLayer from "@arcgis/core/layers/VectorTileLayer";
import esriConfig from "@arcgis/core/config";
import IdentityManager from "@arcgis/core/identity/IdentityManager";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

import {
    FormGroup,
    Label,
    FormFeedback,
    Input,
    ListGroup,
    ListGroupItem,
    ListGroupItemText,
} from "reactstrap";

import $ from "jquery";

export const WebMapView = (props) => {

    if (document.getElementById("pinGeocoder")) {
        document.getElementById("pinGeocoder").setAttribute("problemLocation", props.fstate.problemLocation.value);
        document.getElementById("pinGeocoder").style.color = document.getElementById("pinGeocoder").getAttribute("problemLocation") ? "green" : "red";
    }
    const mapRef = useRef(null);

    var markupEvent = false;
    var pointGraphic = null;

    useEffect(() => {
        defineCustomElements(); // register all arcgis-* web components
        defineCalciteElements(); // register all calcite-* web components used internally by arcgis measurement widgets

        props.updateMapLoad(false);

        var graphicsLayer = new GraphicsLayer();
        graphicsLayer.id = "graphicsLayerSketch"; // assign an id for later reference
        var graphicsLayerArrow = new GraphicsLayer(); //Keep data for arrow graphics
        var graphicsLayerPin = new GraphicsLayer(); // Dedicated layer for the geocoded pin — kept separate from the sketch layer
        var featureLayer = null;
        var markupEvent = false;
        var markupArrow = false;
        var measurmentEvent = false;

        const features = [];

        var _extent = null;
        var pointWkid = null;
        let centerPoint = null;

        if (props.extent != null) _extent = Extent.fromJSON(props.extent);

        if (props.extent != null)
            pointWkid = _extent["spatialReference"]["wkid"];
        else pointWkid = 102100;

        if (props.sp != -1) {
            pointWkid = props.sp;
        }

        if (props.extent != null) {
            centerPoint = new Point({
                x: _extent.center.x.toFixed(4),
                y: _extent.center.y.toFixed(4),
                spatialReference: { wkid: pointWkid },
            });
        }

        try {

            if (props && props.webMapId && props.webMapUrl) {
                esriConfig.portalUrl = props.webMapUrl;

                // FeatureLayer queries execute inside Web Workers, and the interceptors'
                // `before` callbacks are functions that cannot be structured-cloned into a
                // worker — so URL rewrites below never apply to worker-issued query requests,
                // which is why they bypass /ArcGisProxy and hit CORS directly. Forcing every
                // request onto the main thread makes the interceptors apply universally.
                esriConfig.workers.useWorkers = false;

                const webMapOrigin = new URL(props.webMapUrl).origin;
                // Relay all portal requests through the local ASP.NET proxy to avoid browser CORS restrictions.
                // The server forwards them server-to-server where CORS does not apply.
                if (!esriConfig.request.interceptors.some((i) => i._forceAnonymousOrigin === webMapOrigin)) {
                    esriConfig.request.interceptors.push({
                        urls: new RegExp(`^${webMapOrigin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`),
                        _forceAnonymousOrigin: webMapOrigin,
                        before: (params) => {
                            // Guard against params.url being empty/relative at hook time (seen for
                            // some portal bootstrap calls) — rewriting those produced a malformed
                            // "/ArcGisProxy?f=json" request with no target, which the proxy rejects.
                            if (typeof params.url === "string" && /^https?:\/\//i.test(params.url)) {
                                params.url = `/ArcGisProxy?${params.url}`;
                            }
                        }
                    });
                }
                // Prevent the IdentityManager from hanging on an auth challenge for this non-secured portal
                IdentityManager.useSignInPage = false;
                // Block federation discovery so the SDK never marks cross-origin layer servers as
                // "trusted" and attaches credentials to them — that is what causes the intermittent
                // CORS failures when the async federation metadata arrives after the before-hook ran.
                IdentityManager.enabled = false;

                // The SDK marks services federated with the portal as "trusted" and sends
                // withCredentials:true to every cross-origin layer (e.g. app02.cityworksonline.ca).
                // Those servers return Access-Control-Allow-Origin but NOT Allow-Credentials,
                // so the browser blocks the response. Force anonymous for all SDK requests.
                // SDK 5.x requires the `urls` property — interceptors without it are silently skipped.
                if (!esriConfig.request.interceptors.some((i) => i._forceAnonymousGlobal)) {
                    esriConfig.request.interceptors.push({
                        _forceAnonymousGlobal: true,
                        urls: /.*/,
                        before: (params) => {
                            params.requestOptions = params.requestOptions || {};
                            params.requestOptions.withCredentials = false;
                        }
                    });
                }

                // FeatureLayer services inside a WebMap may live on a different host than the portal.
                // The portal-origin interceptor above only covers webMapOrigin; this one catches any
                // remaining cross-origin ArcGIS REST request on any host.
                // `urls` is required in SDK 5.x for the interceptor to be called.
                if (!esriConfig.request.interceptors.some((i) => i._crossOriginArcGisProxy)) {
                    const appOrigin = window.location.origin;
                    esriConfig.request.interceptors.push({
                        _crossOriginArcGisProxy: true,
                        urls: /\/rest\/services\/|\/sharing\/rest\//,
                        before: (params) => {
                            if (
                                typeof params.url === "string" &&
                                /^https?:\/\//i.test(params.url) &&
                                !params.url.startsWith(appOrigin)
                            ) {
                                params.url = `/ArcGisProxy?${params.url}`;
                            }
                        }
                    });
                }

                var map = new WebMap({
                    portalItem: {
                        id: props.webMapId,
                        extent: props.extent,
                    }
                });

                var view = new MapView({
                    map: map,
                    container: mapRef.current,
                    extent: props.extent,
                    /* zoom: 13*/
                });

                map.add(graphicsLayer);
                map.add(graphicsLayerArrow);
                map.add(graphicsLayerPin);
            }
            else {
                console.log("webMapId and webMapUrl are null.");
                var basemap = new Basemap({
                    baseLayers: [
                        new VectorTileLayer({
                            url: props.basemapDefaultUrl,
                            title: "Basemap default"
                        })
                    ]
                });
                var map = new ArcGISMap({
                    basemap: basemap,// "topo-vector",
                    spatialReference: new SpatialReference({ wkid: pointWkid }),
                });
                var view = new MapView({
                    container: mapRef.current,
                    map: map,
                    extent: props.extent,
                    /*  zoom: 13,*/
                });

                map.add(graphicsLayer);
                map.add(graphicsLayerArrow);
                map.add(graphicsLayerPin);
            }
        } catch {
            var basemap = new Basemap({
                baseLayers: [
                    new VectorTileLayer({
                        url: props.basemapDefaultUrl,
                        title: "Basemap defaul"
                    })
                ]
            });
            var map = new ArcGISMap({
                basemap: basemap,//"topo-vector",
                spatialReference: new SpatialReference({ wkid: pointWkid }),
            });
            var view = new MapView({
                container: mapRef.current,
                map: map,
                extent: props.extent,
                /*   zoom: 13,*/
            });

            map.add(graphicsLayer);
            map.add(graphicsLayerArrow);
            map.add(graphicsLayerPin);
        }

        // Allow popup to open when a feature is clicked; suppress it only while
        // the user is actively placing a pin, drawing, or measuring.
        view.popup.autoOpenEnabled = true;
        reactiveUtils.watch(
            () => view.popup.visible,
            (visible) => { if (visible && (mouseEvtHandler || markupEvent || measurmentEvent)) view.popup.close(); }
        );

        // WebMap portal layers load asynchronously and are appended to map.layers
        // after graphicsLayer, burying the pin graphic underneath them.
        // Reorder graphics layers to the top every time a new direct layer is added
        // so the geocoded pin and markup are always rendered above all portal layers.
        if (props.webMapId && props.webMapUrl) {
            map.layers.on("change", ({ added }) => {
                if (!added || added.length === 0) return;
                // Only reorder if the added layer is not one of our own graphics layers
                const addedOther = added.filter(
                    (l) => l !== graphicsLayer && l !== graphicsLayerArrow && l !== graphicsLayerPin
                );
                if (addedOther.length > 0) {
                    try {
                        map.reorder(graphicsLayerArrow, map.layers.length - 1);
                        map.reorder(graphicsLayer, map.layers.length - 1);
                        map.reorder(graphicsLayerPin, map.layers.length - 1);
                    } catch (e) { console.log("reorder error", e); }
                }
            });
        }


        //Add Base Map Gallery web component
        const basemapGalleryEl = document.createElement("arcgis-basemap-gallery");
        basemapGalleryEl.view = view;
        view.ui.add(basemapGalleryEl, { position: "top-right" });
        basemapGalleryEl.addEventListener("arcgisReady", () => {
            basemapGalleryEl.style.display = "none";
        });


        const eventHandler = (evt) => {
            props.listOfAddressCandidates();

            if (!document.getElementById("problemtype").value) {
                props.setAlerts(
                    props.translate(
                        document.getElementById("sr_lang").getAttribute("langVal"),
                        "SelectAProblemTypeMsg"
                    )
                );
                return;
            }
            if (markupEvent == false && measurmentEvent === false) {
                document
                    .getElementById("popup")
                    .setAttribute("point", JSON.stringify(evt.mapPoint));
                getAddressFromXY(evt.mapPoint);
            }
        }


        var mouseEvtHandler = null;

        var elementPin = document.createElement("div");
        elementPin.id = "pinGeocoder";
        elementPin.className = "esri-icon-map-pin esri-widget--button";
        elementPin.style.height = "44px";
        elementPin.style.width = "44px";
        elementPin.style.backgroundColor = "lightgray";
        elementPin.style.color = "red";
        elementPin.title = props.translate(props.lang, "BtnMapGeocoder");

        const enableDisablePinBtn = (elem, option = true) => {

            if (option) {
                // Activating pin mode: hide measurement and sketch tools
                elem.style.backgroundColor = "white";
               //if (document.getElementById("toolbarDiv")) document.getElementById("toolbarDiv").style.display = "none";
                const _sketchHide = document.querySelector("arcgis-sketch");
                if (_sketchHide) _sketchHide.style.display = "none";

            } else {
                // Deactivating pin mode: restore tools only if enabled by props AND map is currently shown
                if (props.useMapMeasurement && document.getElementById("toolbarDiv")) {
                    const _csPinBtn = document.getElementById("core_state");
                    if (_csPinBtn && _csPinBtn.getAttribute("showmapkeyword") === "true") {
                       document.getElementById("toolbarDiv").style.display = "flex";
                    }
                }

                if (props.useMapGraphics) {
                    const _sketchShow = document.querySelector("arcgis-sketch");
                    if (_sketchShow) _sketchShow.style.display = "block";
                }

                elem.style.backgroundColor = "lightgray";
            }

            elem.style.color = document.getElementById("pinGeocoder").getAttribute("problemLocation") ? "green" : "red";
        }



        elementPin.addEventListener("click", function (evt) {
            evt.stopPropagation();

            if (mouseEvtHandler) {
                enableDisablePinBtn(elementPin, false);
                mouseEvtHandler.remove();
                mouseEvtHandler = null;

            } else {
                mouseEvtHandler = view.on("click", eventHandler);
                enableDisablePinBtn(elementPin);

            }

        });
        view.ui.add(elementPin, "top-right");// "bottom-right");





        // Add button to show/hide Base Map Gallery
        var element = document.createElement("div");
        element.className =
            "esri-icon-basemap esri-widget--button esri-widget esri-interactive";
        element.title = "Base Map Gallery";
        element.addEventListener("click", function (evt) {
            evt.stopPropagation();
            basemapGalleryEl.style.display = basemapGalleryEl.style.display === "none" ? "block" : "none";
        });
        view.ui.add(element, "top-left");




        //Add Scale Bar web component
        const scaleBarEl = document.createElement("arcgis-scale-bar");
        scaleBarEl.view = view;
        scaleBarEl.unit = "dual"; // The scale bar displays both metric and non-metric units.
        view.ui.add(scaleBarEl, { position: "bottom-left" });

        //Add Locate web component
        const locateEl = document.createElement("arcgis-locate");
        locateEl.view = view;
        locateEl.useHeadingEnabled = false;
        locateEl.goToOverride = function (view, options) {
            options.target.scale = 1800; // Override the default map scale
            return view.goTo(options.target);
        };
        view.ui.add(locateEl, "top-left");

        //Setup for takeScreenshot
        let pixelRatio = 2;// window.devicePixelRatio;

        var options = {
            width: 2048 * pixelRatio,
            height: 1024 * pixelRatio,
            quality: 100,
            format: "png",//"jpg",
            ignoreBackground: false
        };



        var fillSymbol = new SimpleFillSymbol({
            color: [227, 139, 79, 0.8],
            outline: {
                color: [255, 255, 255],
                width: 1,
            },
        });

        function makeArrowGeometry(
            screenPointStart,
            screenPointEnd,
            view,
            symbol
        ) {
            let start = view.toMap(screenPointStart),
                end = view.toMap(screenPointEnd);

            let dist = start.distance(end),
                dx = end.x - start.x,
                dy = end.y - start.y,
                rads = Math.atan2(dx, dy);

            if (rads < 0) {
                rads = Math.abs(rads);
            } else {
                rads = 2 * Math.PI - rads;
            }

            let width = 0.2 * dist; // 1/5, can adjust
            const cx = start.x, cy = start.y;
            const cos = Math.cos(rads), sin = Math.sin(rads);
            // 2D rotation around the start point (pivot)
            const rot = (x, y) => [
                cx + (x - cx) * cos - (y - cy) * sin,
                cy + (x - cx) * sin + (y - cy) * cos,
            ];

            const ring = [
                rot(cx, cy + dist),           // tip
                rot(cx - width, cy + dist - width),   // edgeLeft
                rot(cx - width / 2, cy + dist - width),   // midLeft
                rot(cx - width / 2, cy),                  // bottomLeft
                rot(cx + width / 2, cy),                  // bottomRight
                rot(cx + width / 2, cy + dist - width),   // midRight
                rot(cx + width, cy + dist - width),   // edgeRight
                rot(cx, cy + dist),           // close
            ];

            return new Graphic({
                geometry: new Polygon({ rings: [ring], spatialReference: { wkid: 102100 } }),
                symbol: symbol,
            });
        }



        //Add Sketch web component
        const sketchEl = document.createElement("arcgis-sketch");
        if (props.useMapGraphics) {
            // Parse availableCreateTools — arrives as a comma-separated string from the API
            const rawTools = props.availableCreateTools;
            const configuredTools = Array.isArray(rawTools)
                ? rawTools
                : typeof rawTools === "string" && rawTools.length > 0
                    ? rawTools.split(",").map((t) => t.trim())
                    : [];

            // Set layer and view BEFORE view.ui.add() so the component can
            // initialize correctly and fire arcgisReady — same pattern as
            // arcgis-basemap-gallery, arcgis-scale-bar, arcgis-locate above.
            sketchEl.layer = graphicsLayer;
            sketchEl.view = view;

            view.ui.add(sketchEl, {
                position: "bottom-right",
            });

            sketchEl.addEventListener("arcgisReady", () => {
                // Configuration flags set AFTER arcgisReady so component defaults
                // don't overwrite them.
                sketchEl.creationMode = "single";
                sketchEl.defaultUpdateOptions = {
                    enableRotation: false,
                    multipleSelectionEnabled: false,
                    toggleToolOnClick: false,
                };

                // hideCreateTools* flags control which buttons are visible.
                // Both v4 names and v5 hyphenated names are handled.
                sketchEl.hideCreateToolsPoint =
                    !configuredTools.includes("point");
                sketchEl.hideCreateToolsPolyline =
                    !configuredTools.includes("polyline") &&
                    !configuredTools.includes("freehandPolyline") &&
                    !configuredTools.includes("freehand-polyline");
                sketchEl.hideCreateToolsPolygon =
                    !configuredTools.includes("polygon") &&
                    !configuredTools.includes("freehandPolygon") &&
                    !configuredTools.includes("freehand-polygon");
                sketchEl.hideCreateToolsRectangle =
                    !configuredTools.includes("rectangle");
                sketchEl.hideCreateToolsCircle =
                    !configuredTools.includes("circle");
                sketchEl.hideCreateToolsMultipoint =
                    !configuredTools.includes("multipoint");
                sketchEl.hideSelectionToolsLassoSelection =
                    !configuredTools.includes("lasso-selection");
                sketchEl.hideSelectionToolsRectangleSelection =
                    !configuredTools.includes("rectangle-selection");
                sketchEl.hideSettingsMenu = true;

                sketchEl.style.display = "block";

            });


            sketchEl.addEventListener("arcgisCreate", function (event) {
                const { state, graphic } = event.detail;
                if (markupArrow) markupArrow = false;
                //Possible Values:"start"|"active"|"complete"|"cancel"

                if (state === "complete" || state === "cancel") {
                    setTimeout(() => {
                        markupEvent = false;
                        let _graphicsLayer = [];

                        view.map.layers.forEach(function (layer) {
                            if (layer.title === "Sketch Layer") {
                                _graphicsLayer = layer.graphics;
                            };

                        });

                        props.setupMarkUpSelected(_graphicsLayer);
                        if (graphic)
                            graphic.symbol.outline.color = [255, 0, 0, 1];

                        /* props.pageTwoValidationClick();*/
                        if (_graphicsLayer.length > 0) {
                            sketchEl.style.setProperty("--calcite-action-text-color", "black"); // reset to default for non-active tools while drawing
                        } else {

                            let pbTypeReq = "false";
                            if (document.getElementById("problemSelectLabel") &&
                                document
                                    .getElementById("problemSelectLabel")
                                    .getAttribute("drawingRequiredForTemplateKeywordFound")) {
                                pbTypeReq = document
                                    .getElementById("problemSelectLabel")
                                    .getAttribute("drawingRequiredForTemplateKeywordFound")
                                    .toString()
                                    .toLowerCase();
                            }

                            if (pbTypeReq === "true") {
                                sketchEl.style.setProperty("--calcite-action-text-color", "red");
                            }
                        }
                    }, 0);
                }

                if (state === "start") {
                    markupEvent = true;
                    if (view.popup) view.popup.visible = false;
                    // props.setupMarkUpSelected([]);
                }
                if (state === "active") {
                    //console.log("sketch active", event);
                }

            });

            sketchEl.addEventListener("arcgisDelete", function (event) {
                // event.detail.graphics contains the deleted graphics
                setTimeout(() => {
                    let _graphicsLayer = [];

                    view.map.layers.forEach(function (layer) {
                        if (layer.title === "Sketch Layer") {
                            _graphicsLayer = layer.graphics;
                        };
                    });
                    props.setupMarkUpSelected((_graphicsLayer && _graphicsLayer.length > 0) ? _graphicsLayer : null);
                    if (_graphicsLayer.length > 0) {
                        sketchEl.style.setProperty("--calcite-action-text-color", "black"); // reset to default for non-active tools while drawing
                    } else {
                        let pbTypeReq = "false";
                        if (document.getElementById("problemSelectLabel") &&
                            document
                                .getElementById("problemSelectLabel")
                                .getAttribute("drawingRequiredForTemplateKeywordFound")) {
                            pbTypeReq = document
                                .getElementById("problemSelectLabel")
                                .getAttribute("drawingRequiredForTemplateKeywordFound")
                                .toString()
                                .toLowerCase();
                        }

                        if (pbTypeReq === "true") {
                            sketchEl.style.setProperty("--calcite-action-text-color", "red");
                        }
                    }

                    /*  props.pageTwoValidationClick();*/
                }, 0);

            });

            sketchEl.addEventListener("arcgisUpdate", function (event) {
                const { state } = event.detail;
                if (state === "complete") {
                    setTimeout(() => {
                        let _graphicsLayer = [];

                        view.map.layers.forEach(function (layer) {
                            if (layer.title === "Sketch Layer") {
                                _graphicsLayer = layer.graphics;
                            };
                        });

                        props.setupMarkUpSelected(_graphicsLayer);
                        if (_graphicsLayer.length > 0) {
                            sketchEl.style.setProperty("--calcite-action-text-color", "black"); // reset to default for non-active tools while drawing
                        } else {
                            let pbTypeReq = "false";
                            if (document.getElementById("problemSelectLabel") &&
                                document
                                    .getElementById("problemSelectLabel")
                                    .getAttribute("drawingRequiredForTemplateKeywordFound")) {
                                pbTypeReq = document
                                    .getElementById("problemSelectLabel")
                                    .getAttribute("drawingRequiredForTemplateKeywordFound")
                                    .toString()
                                    .toLowerCase();
                            }

                            if (pbTypeReq === "true") {
                                sketchEl.style.setProperty("--calcite-action-text-color", "red");
                            }
                        }

                        /*  props.pageTwoValidationClick();*/
                    }, 0);
                }
            });

            const _rawTools = props.availableCreateTools;
            const _toolList = Array.isArray(_rawTools)
                ? _rawTools
                : typeof _rawTools === "string" && _rawTools.length > 0
                    ? _rawTools.split(",").map((t) => t.trim())
                    : [];
            if (_toolList.indexOf("arrow") !== -1) {
                var elementDrawArrow = document.createElement("div");
                elementDrawArrow.id = "sketch-arrow";

                //elementDrawArrow.style.borderRight = "1px solid lightgray";
                elementDrawArrow.style.margin = "0px 5px 0px 0px";
                elementDrawArrow.className =
                    "esri-icon-overview-arrow-top-left esri-widget--button esri-widget esri-interactive customBtn";
                view.ui.add(elementDrawArrow, "bottom-right");
                elementDrawArrow.addEventListener("click", function (evt) {
                    evt.stopPropagation();
                    markupArrow = true;
                    //if (markupEvent === false) return;
                    view.on("drag", (e) => {
                        e.stopPropagation();
                        if (markupArrow === false) return;
                        if (e.origin.x !== e.x && e.origin.y !== e.y) {
                            let arrow = makeArrowGeometry(
                                e.origin,
                                { x: e.x, y: e.y },
                                view,
                                fillSymbol
                            );
                            graphicsLayerArrow.removeAll();
                            graphicsLayerArrow.add(arrow);

                        }
                    });
                });
                document
                    .getElementById("sketch-arrow")
                    .setAttribute("title", "Draw arrow");
            }

            // sketchEl.addEventListener("arcgisReady", () => {
            //     // Arrow button remains in view.ui; shadow DOM internals cannot be accessed
            //     // Note: hide flags and availableCreateTools are set in the first arcgisReady above
            // });

        }

        if (props.useMapMeasurement) {   
            // Create toolbar container dynamically (like other UI elements)
            var toolbarDiv = document.createElement("div");
            toolbarDiv.id = "toolbarDiv";
            toolbarDiv.className = "esri-component esri-widget";
            
            // Create inner container for flex layout
            var toolbarInner = document.createElement("div");
            toolbarInner.style.display = "flex";
            
            // Create distance button
            const distanceButton = document.createElement("button");
            distanceButton.id = "distance";
            distanceButton.className = "esri-widget--button esri-interactive esri-icon-measure-line";
            distanceButton.title = "Distance Measurement Tool";
            
            // Create area button
            const areaButton = document.createElement("button");
            areaButton.id = "area";
            areaButton.className = "esri-widget--button esri-interactive esri-icon-measure-area";
            areaButton.title = "Area Measurement Tool";
            
            // Create clear button
            const clearButton = document.createElement("button");
            clearButton.id = "clear";
            clearButton.className = "esri-widget--button esri-interactive esri-icon-trash";
            clearButton.title = "Clear Measurements";
            
            // Assemble the toolbar
            toolbarInner.appendChild(distanceButton);
            toolbarInner.appendChild(areaButton);
            toolbarInner.appendChild(clearButton);
            toolbarDiv.appendChild(toolbarInner);
            
            // Add to map view UI
            view.ui.add(toolbarDiv, "bottom-right");

            // Set-up event handlers for buttons and click events

            if (distanceButton) distanceButton.addEventListener("click", () => {
                measurmentEvent = true;
                if (view.popup) view.popup.visible = false;
                distanceMeasurement();
            });
            if (areaButton) areaButton.addEventListener("click", () => {
                measurmentEvent = true;
                if (view.popup) view.popup.visible = false;
                areaMeasurement();
            });
            if (clearButton) clearButton.addEventListener("click", () => {
                measurmentEvent = false;
                clearMeasurements();
            });

            // Replace deprecated arcgis-measurement with individual v5 components
            // Placed at top-right to avoid overlapping the sketch and scale-bar widgets at the bottom
            const distanceMeasurementEl = document.createElement("arcgis-distance-measurement-2d");
            distanceMeasurementEl.view = view;
            distanceMeasurementEl.hideStartButton = true; // start is controlled by toolbar buttons
            distanceMeasurementEl.unit = "metric"; // pre-select a default so the unit dropdown stays compact
            view.ui.add(distanceMeasurementEl, "top-right");

            const areaMeasurementEl = document.createElement("arcgis-area-measurement-2d");
            areaMeasurementEl.view = view;
            areaMeasurementEl.hideStartButton = true; // start is controlled by toolbar buttons
            areaMeasurementEl.unit = "metric"; // pre-select a default so the unit dropdown stays compact
            view.ui.add(areaMeasurementEl, "top-right");

            // Hide both initially — shown on button click
            distanceMeasurementEl.addEventListener("arcgisReady", () => {
                distanceMeasurementEl.style.display = "none";
                // const tb = document.getElementById("toolbarDiv");
                // if (tb) {
                //     tb.style.flexDirection = "column";
                //     tb.style.border = "5px solid white";
                //     // Use the DOM showmapkeyword attribute — avoids stale closure on props.fstate
                //     const _cs = document.getElementById("core_state");
                //     tb.style.display = (_cs && _cs.getAttribute("showmapkeyword") === "true")
                //         ? "flex" : "none";
                // }
            });
            areaMeasurementEl.addEventListener("arcgisReady", () => {
                areaMeasurementEl.style.display = "none";
            });

            distanceMeasurementEl.addEventListener("arcgisPropertyChange", function () {
                const state = distanceMeasurementEl.state;
                if (state === "measuring") {
                    measurmentEvent = true;
                }
                if (state === "measured" || state === "ready") {
                    // Only clear the flag if area is also not actively measuring
                    if (areaMeasurementEl.state !== "measuring") {
                        measurmentEvent = false;
                    }
                }
            });

            areaMeasurementEl.addEventListener("arcgisPropertyChange", function () {
                const state = areaMeasurementEl.state;
                if (state === "measuring") {
                    measurmentEvent = true;
                }
                if (state === "measured" || state === "ready") {
                    // Only clear the flag if distance is also not actively measuring
                    if (distanceMeasurementEl.state !== "measuring") {
                        measurmentEvent = false;
                    }
                }
            });

            distanceMeasurementEl.addEventListener("arcgisReady", () => {
                // const tb = document.getElementById("toolbarDiv");
                // if (tb) {
                //     tb.style.flexDirection = "column";
                //     tb.style.border = "5px solid white";
                //     // Use the DOM showmapkeyword attribute — avoids stale closure on props.fstate
                //     const _cs = document.getElementById("core_state");
                //     tb.style.display = (_cs && _cs.getAttribute("showmapkeyword") === "true")
                //         ? "flex" : "none";
                // }
            });

            // file deepcode ignore FunctionDeclarationInBlock: <>
            function distanceMeasurement() {
                measurmentEvent = true;
                areaMeasurementEl.style.display = "none";
                areaMeasurementEl.clear();
                distanceMeasurementEl.style.display = "block";
                distanceMeasurementEl.start();
                if (distanceButton) distanceButton.classList.add("active");
                if (areaButton) areaButton.classList.remove("active");
            }

            function areaMeasurement() {
                measurmentEvent = true;
                distanceMeasurementEl.style.display = "none";
                distanceMeasurementEl.clear();
                areaMeasurementEl.style.display = "block";
                areaMeasurementEl.start();
                if (distanceButton) distanceButton.classList.remove("active");
                if (areaButton) areaButton.classList.add("active");
            }

            // Clears all measurements
            function clearMeasurements() {
                measurmentEvent = false;
                if (distanceButton) distanceButton.classList.remove("active");
                if (areaButton) areaButton.classList.remove("active");
                distanceMeasurementEl.style.display = "none";
                areaMeasurementEl.style.display = "none";
                distanceMeasurementEl.clear();
                areaMeasurementEl.clear();
            }
            // const measurementEl = document.createElement("arcgis-measurement");
            // measurementEl.view = view;
            // measurementEl.style.display = "none"; // UI driven by custom toolbarDiv buttons
            // document.getElementById("toolbarDiv").appendChild(measurementEl);

            // measurementEl.addEventListener("arcgisPropertyChange", function () {
            //     const state = measurementEl.state;
            //     if (state === "ready") {
            //         measurmentEvent = true;
            //     }
            //     if (state === "measured") {
            //         measurmentEvent = false;
            //     }
            // });

            // measurementEl.addEventListener("arcgisReady", () => {
            //     document.getElementById("toolbarDiv").style.display = "flex"; //horizontal layout
            //     document.getElementById("toolbarDiv").style.border =
            //         "5px solid white";
            //     document.getElementById("toolbarDiv").style.height = "45px";
            // });

            // // Call the appropriate DistanceMeasurement2D or DirectLineMeasurement3D
            // // file deepcode ignore FunctionDeclarationInBlock: <>
            // function distanceMeasurement() {
            //     measurmentEvent = true;
            //     // if (markupEvent === false) return;

            //     if (measurementEl) measurementEl.activeTool = "distance"; // type.toUpperCase() === "2D" ? "distance" : "direct-line";
            //     if (distanceButton) distanceButton.classList.add("active");
            //     if (areaButton) areaButton.classList.remove("active");
            // }

            // // Call the appropriate AreaMeasurement2D or AreaMeasurement3D
            // function areaMeasurement() {
            //     measurmentEvent = true;
            //     //  if (markupEvent === false) return;
            //     if (measurementEl) measurementEl.activeTool = "area";
            //     if (distanceButton) distanceButton.classList.remove("active");
            //     if (areaButton) areaButton.classList.add("active");
            // }

            // // Clears all measurements
            // function clearMeasurements() {
            //     measurmentEvent = false;
            //     /* if (markupEvent === false) return;*/
            //     if (distanceButton) distanceButton.classList.remove("active");
            //     if (areaButton) areaButton.classList.remove("active");
            //     if (measurementEl) measurementEl.clear();
            // }

            // Clear any measurements that had been drawn
            clearMeasurements();
        }



        const centerYRequest = {
            inSR: pointWkid,
            outSR: 102100,
            geometries: JSON.stringify({
                geometryType: "esriGeometryPoint",
                geometries: [centerPoint],
            }),
            userSettingsId: props.userSettingsId
        };

        const requestOptionsCenter = {
            method: "POST",
            headers: {
                Authorization: "Bearer " + props.token,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(centerYRequest),
        };
        try {
            window
                .fetch(`GetExtentCenter`, requestOptionsCenter)
                .then((resp) => {
                    if (!resp.ok && resp.status == 401) {
                        props.loadDiv("hide");
                        props.setAlerts(`Unauthorized. Token may have expired.`, "danger");
                        throw new Error(`Unauthorized. Token may have expired.`);
                    }

                    return resp.json();
                })
                .catch((error) => {
                    props.loadDiv("hide"); console.log(error);
                    throw new Error(error);
                })
                .then((data) => {
                    try {
                        if (data.status !== 0) {
                            props.setAlerts(data.message, "danger");
                        } else {
                            if (data && data.value) {
                                let value = data.value;

                                let _wp = webMercatorUtils.xyToLngLat(value["x"], value["y"]);
                                view.center = _wp;
                            }
                        }
                    } catch (error) {
                        props.loadDiv("hide");
                        console.log("Error: ", error);
                        props.setAlerts(error, "danger");
                    }
                })
                .catch((error) => { console.log(error) });;
        } catch (error) {
            props.loadDiv("hide");
            console.log("Error: ", error);
            setAlert(error, "danger");
        }

        const getAddressFromXY = (point) => {

            props.loadDiv("show");

            drawPoint(point, true);


            let pointjson = {
                geometryType: "esriGeometryPoint",
                geometries: [point.toJSON()],
            };
            let mapWkid = point.toJSON()["spatialReference"]["wkid"];



            props.getLocation(point, pointjson, mapWkid, pointWkid);


        };

        var elRunXYfromAddress = document.createElement("div");
        elRunXYfromAddress.id = "elementRunXYfromAddress";
        elRunXYfromAddress.className =
            "esri-icon-overview-arrow-top-left esri-widget--button esri-widget esri-interactive";
        view.ui.add(elRunXYfromAddress, "bottom-right");
        $("#elementRunXYfromAddress").css("display", "none");


        elRunXYfromAddress.addEventListener("keyup", function (e) {
            e.stopPropagation();
            let sellectedAddress = document
                .getElementById("elementRunXYfromAddress")
                .getAttribute("sellectedAddress");
            let X = parseFloat(
                document.getElementById("elementRunXYfromAddress").getAttribute("x")
            );
            let Y = parseFloat(
                document.getElementById("elementRunXYfromAddress").getAttribute("y")
            );

            let _wp = webMercatorUtils.xyToLngLat(X, Y);

            drawPoint(new Point({ x: X, y: Y, spatialReference: { wkid: pointWkid } }), true);

            document
                .getElementById("elementRunXYfromAddress")
                .setAttribute("sellectedAddress", "");
            document
                .getElementById("elementRunXYfromAddress")
                .setAttribute("x", "-1");
            document
                .getElementById("elementRunXYfromAddress")
                .setAttribute("y", "-1");

            var point = new Point({
                x: X,
                y: Y,
                spatialReference: { wkid: pointWkid },
            });
            let mapWkid = pointWkid;

            props.getAddress(point, pointWkid, mapWkid, sellectedAddress, X, Y);
        });



        function drawPoint(point, isZoom = false) {
            if (pointGraphic) {
                graphicsLayerPin.remove(pointGraphic);
            }

            // Use the point's native SR — the view SR is wkid 28017 (not 102100),
            // so converting to lng/lat and back to a hardcoded SR causes the graphic
            // to be placed at the wrong location or not render at all.
            pointGraphic = new Graphic({
                geometry: point,
                symbol: new SimpleMarkerSymbol({
                    style: "circle",
                    color: [226, 119, 40], // orange
                    size: 12,
                    outline: new SimpleLineSymbol({
                        color: [255, 255, 255], // white
                        width: 1,
                    }),
                }),
            });

            graphicsLayerPin.add(pointGraphic);

            if (mouseEvtHandler) {
                mouseEvtHandler.remove();
                mouseEvtHandler = null;
            }
            let pin = document.getElementById("pinGeocoder");
            enableDisablePinBtn(pin, false);

            try {
                const options = { target: pointGraphic };
                if (isZoom) {
                    if (props.webMapId && props.webMapUrl) {
                        // WebMaps with custom SR (e.g. wkid 28017) don't use standard
                        // Web Mercator LODs, so zoom: N gives unpredictable results.
                        // Use scale instead: if geoCodeZoomLevel looks like a scale value
                        // (> 100) honour it, otherwise default to 10000 (~2 km view).
                        options.scale = props.geoCodeZoomLevel > 100
                            ? props.geoCodeZoomLevel
                            : 10000;
                    } else {
                        options.zoom = props.geoCodeZoomLevel;
                    }
                }
                view.goTo(options).catch((e) => { console.error("goTo error:", e); });
            } catch (e) { console.error("goTo sync error:", e); }
        }



        const popupAddressClick = (e) => {

            e.stopPropagation();
            let address_x = parseFloat(
                document.getElementById("popup_address").getAttribute("address_x")
            );
            let address_y = parseFloat(
                document.getElementById("popup_address").getAttribute("address_y")
            );

            // address_x/address_y are stored in SR 102100 (outSR from GetExtentCenter)
            drawPoint(new Point({ x: address_x, y: address_y, spatialReference: { wkid: 102100 } }), true);
            view.center = new Point({ x: address_x, y: address_y, spatialReference: { wkid: 102100 } });
            props.loadDiv("hide");

        };



        const screenShootClick = (e) => {
            e.stopPropagation();

            if (view != null) {
                try {
                    view.takeScreenshot(options).then(function (screenshot) {
                        try {
                            document.getElementById("screenshot_thumbnail_id").src =
                                screenshot.dataUrl;

                            props.saveScreenshot(screenshot.dataUrl);
                        } catch (ex) {
                            console.log(ex);
                            props.loadDiv("hide");
                        }
                    })
                        .catch((error) => { console.log(error) });
                } catch { }
            }
        }



        const destroyGraphics = () => {
            graphicsLayer.removeAll();
            graphicsLayerArrow.removeAll();
            graphicsLayerPin.removeAll();
            pointGraphic = null;
            props.setupMarkUpSelected([]);
            view.map.layers.forEach(function (layer) {
                if (layer.title === "Sketch Layer") {
                    layer.graphics.removeAll();
                };
            });
        };


        view
            .when(() => {
                // When a WebMap is used, portal layers are added asynchronously after
                // graphicsLayer, pushing it below them in the render order and hiding
                // the geocoded pin. Reorder both graphics layers to the top so the pin
                // and markup graphics are always visible above all operational layers.
                if (props.webMapId && props.webMapUrl) {
                    map.reorder(graphicsLayerArrow, map.layers.length - 1);
                    map.reorder(graphicsLayer, map.layers.length - 1);
                    map.reorder(graphicsLayerPin, map.layers.length - 1);
                }

                document.getElementById("screenshot")?.addEventListener("keyup", screenShootClick, true);
                document.getElementById("destroy_graphics")?.addEventListener("keyup", destroyGraphics, true);
                document.getElementById("popup_address")?.addEventListener("keyup", popupAddressClick, true);
                // document.getElementById("popup")?.addEventListener("keyup", popupClick, true);


                // Update the drawingRequired flag for form validation purposes only;
                // sketch widget visibility is controlled by useMapGraphics / pin mode, not here.
                if (props.drawingRequiredForAllTypes === false) {
                    const problemValue = document.getElementById("problemtype").value;
                    const prob = problemValue && props.problemTypes.find(
                        (f) => f.description === problemValue
                    );
                    const kwFound = !!(
                        prob &&
                        prob.keywords &&
                        prob.keywords.indexOf(props.drawingRequiredForTemplateKeyword) !== -1
                    );
                    if (document.getElementById("problemSelectLabel")) {
                        document
                            .getElementById("problemSelectLabel")
                            .setAttribute("drawingRequiredForTemplateKeywordFound", kwFound);
                    }
                } else {
                    if (document.getElementById("problemSelectLabel")) {
                        document
                            .getElementById("problemSelectLabel")
                            .setAttribute("drawingRequiredForTemplateKeywordFound", true);
                    }
                }
                props.updateMapLoad(true);
            })
            .catch();

        return () => {
            document.getElementById("screenshot")?.removeEventListener("keyup", screenShootClick, true);
            document.getElementById("destroy_graphics")?.removeEventListener("keyup", destroyGraphics, true);
            document.getElementById("popup_address")?.removeEventListener("keyup", popupAddressClick, true);
            // document.getElementById("popup")?.removeEventListener("keyup", popupClick, true);



            if (view) {
                view.container = null;
            }
        };

        //if (props.fstate.problemLocation.value) {
        //    document.getElementById("popup").dispatchEvent(new KeyboardEvent('keyup', { 'key': 'y' })); 
        //}
    }, [props.refresh]);

    // Suggestion items (from AddressSuggestions) only carry a magic key and need a follow-up
    // GeocodeXY lookup for X/Y; legacy candidates (fallback GeocodeXY) already have X/Y.
    const applyAddressCandidate = async (a) => {
        props.listOfAddressCandidates();

        let x, y, selectedAddress;

        if (Object.prototype.hasOwnProperty.call(a, "suggestedAddress")) {
            const location = await props.getLocationByMagicKey(a.suggestedAddress, a.magicKey);
            if (!location) return;
            x = location.x;
            y = location.y;
            selectedAddress = location.address;
        } else {
            x = a.X;
            y = a.Y;
            selectedAddress = a[props.addressField];
        }

        document.getElementById("elementRunXYfromAddress").setAttribute("x", x.toString());
        document.getElementById("elementRunXYfromAddress").setAttribute("y", y.toString());
        document.getElementById("elementRunXYfromAddress").setAttribute("sellectedAddress", selectedAddress);
        document.getElementById("elementRunXYfromAddress").dispatchEvent(new KeyboardEvent('keyup', { 'key': 'y' }));
    };

    // Bolds the portion of an address suggestion that matches the current search text
    const highlightAddressMatch = (text, search) => {
        if (!search || !text) return text;
        const idx = text.toLowerCase().indexOf(search.toLowerCase());
        if (idx === -1) return text;
        return (
            <>
                {text.substring(0, idx)}
                <strong>{text.substring(idx, idx + search.length)}</strong>
                {text.substring(idx + search.length)}
            </>
        );
    };

    return (
        <>
            {props &&
                props.fstate &&
                props.fstate.problemLocation &&
                props.fstate.problemLocation.disable ? (
                ""
            ) : (
                <FormGroup>


                    <div id="mapInstructions" className="hideElement">
                        {/* <Label className="line"></Label> */}
                        <div className="display-linebreak">
                            {props.translate(props.lang, props.fstate.mapInstructions.label)}
                        </div>
                        {/* <Label className="line"></Label>{" "} */}
                    </div>


                    <ListGroup className="flex-select1">
                        {/*<PRInput*/}
                        {/*    elem={props.fstate.problemLocation}*/}
                        {/*    validate={(e) => {*/}
                        {/*        props.validate(e);*/}
                        {/*    }}*/}
                        {/*    translate={props.translate}*/}
                        {/*    placeholder={props.fstate.problemLocation.placeholder}*/}
                        {/*    lang={props.lang}*/}
                        {/*/>*/}
                        <div style={{ position: "relative" }}>
                            <Input
                                key={props.fstate.problemLocation.name}
                                type="input"
                                autoComplete="off"
                                name={props.fstate.problemLocation.name}
                                id={props.fstate.problemLocation.name}
                                placeholder={
                                    props.fstate.problemLocation.placeholder
                                        ? props.translate(
                                            props.lang,
                                            props.fstate.problemLocation.placeholder
                                        )
                                        : ""
                                }
                                value={props.fstate.problemLocation.valueDesc}

                                valid={(document.getElementById("pinGeocoder") && document.getElementById("pinGeocoder").getAttribute("problemLocation")) ? true : false}
                                invalid={!(document.getElementById("pinGeocoder") && document.getElementById("pinGeocoder").getAttribute("problemLocation")) ? true : false}
                                maprefresh={props.maprefresh}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    if ($(".listAddress.stack-top"))
                                        $(".listAddress.stack-top").css("display", "none");

                                    document.getElementById("destroy_graphics").dispatchEvent(new KeyboardEvent('keyup', { 'key': 'y' }));
                                    props.getAddreses(e);
                                }}
                            />
                            {props.fstate.problemLocation.required && (!props.fstate.problemLocation.value || props.fstate.problemLocation.value === "") ? (
                                <div className="warningmsg">
                                    {props.fstate.problemLocation.errorlabel && props.fstate.problemLocation.errorlabel.length > 0 ? props.translate(props.lang, props.fstate.problemLocation.errorlabel)
                                        : props.translate(props.lang, props.fstate.problemLocation.label).replace(':', '') + " " + props.translate(props.lang, "isRequired") + '. '}
                                </div>
                            ) : ""}

                            {(props.gisAddreses && props.gisAddreses.length > 0) || props.noAddressResultsFound ? (
                                <div className="listAddress stack-top mapAddressStack">
                                    {props.noAddressResultsFound ? (
                                        <div className="flex-select-list-item noResultsFound">
                                            {props.translate(props.lang, "NoDataFound")}
                                        </div>
                                    ) : (
                                        props.gisAddreses.map((a, index) => (
                                            <div
                                                className="flex-select-list-item"
                                                key={props.fstate.problemLocation.name + index + "div"}
                                            >
                                                <ListGroupItem
                                                    tag="button"
                                                    action
                                                    key={props.fstate.problemLocation.name + index + "el"}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        props.clearAlerts();
                                                        applyAddressCandidate(a);
                                                        e.preventDefault();
                                                    }}
                                                    onKeyUp={(e) => {
                                                        e.stopPropagation();
                                                        props.clearAlerts();
                                                        applyAddressCandidate(a);
                                                        e.preventDefault();
                                                    }}
                                                >
                                                    <ListGroupItemText>
                                                        {highlightAddressMatch(
                                                            Object.prototype.hasOwnProperty.call(a, "suggestedAddress")
                                                                ? a.suggestedAddress
                                                                : a[props.addressField],
                                                            props.fstate.problemLocation.valueDesc
                                                        )}
                                                    </ListGroupItemText>

                                                </ListGroupItem>
                                            </div>
                                        ))
                                    )}
                                </div>
                            ) : (
                                ""
                            )}
                        </div>
                    </ListGroup>

                    <FormFeedback valid></FormFeedback>
                    <FormFeedback>
                        {props.fstate.problemLocation.errorlabel &&
                            props.fstate.problemLocation.errorlabel.length > 0
                            ? props.translate(
                                props.lang,
                                props.fstate.problemLocation.label
                            ) +
                            " " +
                            props.translate(props.lang, "isRequired") +
                            " " +
                            (props.errmsg ? props.translate(props.lang, props.errmsg) : "")
                            : ""}
                    </FormFeedback>
                </FormGroup>
            )}

            <div id="_map_list">
                <button id="destroy_graphics" className="hideElement"></button>
                <button id="popup" className="hideElement"></button>
                <button id="screenshot" className="hideElement"></button>
                <button id="popup_address" className="hideElement"></button>
                <div className="webmap" ref={mapRef} id="map">
                    {" "}

                </div>


                <div
                    id="btnGraphicsActions"
                    className={
                        props.useMapGraphics
                            ? "btnsContainerRow"
                            : "btnsContainerRow div_hidden"
                    }
                >
                </div>
            </div>
        </>
    );
};
